import gsap from 'gsap'
import { useState } from 'react'
import { useGSAP } from '@gsap/react'
import { useRequest, useView } from '@10000/react'
import { Body, Box, FinishQuestionDialog, H3, H4, Icon, Meter, ParticipantsDialog } from '../../widgets'
import { SplashScreen, useBackground, useShell } from '../../layout'
import { questions } from '../../../lookups'
import { SquizButton, SquizScreen, usePracticeWheel } from '../common'
import { Splash } from './Splash.jsx'
import './TeamAverage.scss'

export const TeamAverage = ({ active, initialRender }) => {
  const request = useRequest('squiz')
  const { spin, hide, update } = usePracticeWheel()
  const { questionIndex, question, presenter, playersAnswered, teamSize, answers, options, teamAverageComplete } = useView('squiz')
  const { presenterParticipates, kiosk, questionCount, consensus, solo } = options || {}

  const [meter, setMeter] = useState(answers?.[question])
  const [shownAnswer, setShownAnswer] = useState(answers?.[question])
  const [spinning, setSpinning] = useState(true)
  const [participantsOpen, setParticipantsOpen] = useState(false)
  const [finishDialogOpen, setFinishDialogOpen] = useState(false)
  const playing = presenterParticipates || !presenter || kiosk

  const [lockedIn, setLockedIn] = useState(playing && answers?.[question] !== null)
  const [splash, setSplash] = useState(!lockedIn && !initialRender)

  useShell({
    active,
    mode: 'squiz' + (splash ? '' : ` ${presenter ? 'narrow' : ''} left-aligned`),
    step: questionIndex + 1,
    max: questionCount
  })

  useBackground().set(mobile => ({
    active,
    background: (splash && !spinning) ? 1 : null,
    position: mobile ? 'left -756px top -472px' : 'left -316px top -710px',
    size: '2072px 2034px',
    fade: true
  }))

  update({ pb: 0 })

  useGSAP(() => {
    if(!lockedIn && !teamAverageComplete) {
      spin()
        .to('body', { delay: 1 })
        .call(() => setSpinning(false))
        .call(hide)
        .to('.question-container h4', { opacity: 1, duration: 0.5 })
        .to('body', { delay: 2 })
        .call(() => setSplash(false))
    }
  })

  const completeTeamAverage = () => {
    setFinishDialogOpen(false)
    request('teamAverageComplete')
  }

  const handleCompleteTeamAverage = () => {
    if(playersAnswered !== teamSize) {
      setFinishDialogOpen(true)
    } else {
      completeTeamAverage()
    }
  }

  const handleLockedIn = () => request('participantAnswered', { answer: meter }).then(() => setLockedIn(true))
  const handleUnlocked = () => {
    request('participantAnswered', { answer: null })
    setLockedIn(false)
  }

  const handleMeterChange = (answer, isMouseUp) => {
    if(lockedIn && presenter) {
      handleUnlocked()
    }

    if(!lockedIn || presenter) {
      setShownAnswer(answer)

      if(isMouseUp){
        setMeter(answer)
      }
    }
  }

  const skipAnimation = () => {
    setSplash(false)
    hide()
    gsap.to('.question-container h4', { opacity: 1, duration: 0.5 })
  }

  return (
    <SquizScreen participants={!splash && presenter}>
      <Box
        container
        column
        gap={32}
        fullWidth
        fullHeight
        className="question-container"
        justifyContent="space-between"
      >
        <Box
          container
          column
          gap={32}
          height={200}
          top={splash ? 200 : 0}
          transition={0.5}
          alignSelf="center"
          className={splash ? 'splash' : undefined}
          onClick={skipAnimation}
          zIndex={((!lockedIn && !teamAverageComplete) || !playing) ? 2 : 0}
          opacity={((!lockedIn && !teamAverageComplete) || !playing) ? 1 : 0}
        >
          <H4 align="center" opacity={0}>
            {questions[question]?.question}
          </H4>
          <Body
            align="center"
            opacity={splash ? 0 : 1}
            style={{ transition: '0.5s' }}
            className={(shownAnswer === null && !splash) ? 'blink' : undefined}
          >
            {shownAnswer !== null ? questions[question]?.answers[shownAnswer] : 'Touch meter to start'}
          </Body>
        </Box>

        <Box
          column
          fullWidth
          gap={8}
          opacity={splash ? 0 : 1}
          position="absolute"
          maxWidth={600}
          top={(lockedIn || teamAverageComplete) ? 40 : 'min(50%, calc(100% - min(calc((100vw - 64px) / 2), 400px))'}
          zIndex={1}
          transition={0.5}
        >
          <Meter value={meter} onChange={handleMeterChange} integer disabled={(lockedIn && !presenter) || (teamAverageComplete)} />
          <Box fullWidth justifyContent="space-between" alignItems="center">
            <Box alignItems="center" justifyContent="center">
              <Icon icon="Team" size={16}/>
              <Body lineHeight="150%" size={12}>{playersAnswered}/{teamSize} RESPONDED</Body>
            </Box>
            {(presenter && !solo) &&
              <Box className="mobile-only" alignItems="center" onClick={() => setParticipantsOpen(true)}>
                <Body bold underline alt italic>Manage users</Body>
              </Box>
            }
          </Box>
        </Box>

        <Box
          column
          position="absolute"
          inset
          justifyContent="space-between"
          opacity={((lockedIn || teamAverageComplete) && playing) ? 1 : 0}
          transition={0.5}
          pb={80}
        >
          <Box height="min(300px, calc(100vw / 2))" />
          <H3 align="center">{teamAverageComplete ? 'Question complete' : 'Locked in'}</H3>
          {(!teamAverageComplete && presenter) &&
            <Body align="center" className="blink">Touch meter to change</Body>
          }
          <Body
            alt
            bold
            opacity={0}
            align="center"
            className={playersAnswered !== teamSize ? 'blink' : undefined}
          >
            Waiting for {teamAverageComplete ? 'host' : 'others'}
          </Body>
        </Box>

        <Box fullWidth opacity={splash ? 0 : 1} transition={0.5} zIndex={2}>
          {(((!lockedIn && presenter) || !presenter) && playing) &&
            <SquizButton
              onClick={lockedIn ? handleUnlocked : handleLockedIn}
              disabled={meter === null || teamAverageComplete}
              text={lockedIn ? 'Tap to unlock' : 'Lock it in'}
              hideTools={!presenter}
            />
          }
          {(presenter && (!playing || lockedIn)) &&
            <SquizButton
              onClick={handleCompleteTeamAverage}
              text={consensus ? 'Complete question' : 'Reveal result'}
            />
          }
        </Box>
      </Box>

      {presenter &&
        <ParticipantsDialog open={participantsOpen} onClose={() => setParticipantsOpen(false)} />
      }

      <SplashScreen open={presenter && teamAverageComplete}>
        <Splash active={presenter && teamAverageComplete} />
      </SplashScreen>

      <FinishQuestionDialog
        open={finishDialogOpen}
        onClose={() => setFinishDialogOpen(false)}
        onContinue={completeTeamAverage}
        showControls={false}
      />
    </SquizScreen>
  )
}